import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState, StatusBadge, SectionCard } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

function EscalatedComplaintsInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resolving, setResolving] = useState(null);
  const [note, setNote] = useState('');
  const [resolveTarget, setResolveTarget] = useState(null);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    api.get('/higher-authority/escalated-complaints')
      .then(res => setComplaints(res.data))
      .catch(e => setError(e.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const handleResolve = async (id) => {
    setResolving(id);
    try {
      await api.post('/higher-authority/resolve-complaint/' + id, { resolution_note: note });
      setComplaints(prev => prev.filter(c => c.id !== id));
      setResolveTarget(null);
      setNote('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to resolve');
    } finally { setResolving(null); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : 248 }}>
          <Topbar title='Escalated Complaints' subtitle='Complaints escalated to Higher Authority'
            onMobileMenu={() => setMobileOpen(true)}
            actions={<button className='ur-btn-hover' style={s.btnSecondary} onClick={() => navigate('/higher-authority/dashboard')}>Dashboard</button>} />
          <div style={s.content}>
            <PageHeader icon={"⚠️"} title='Escalated Complaints'
              subtitle='These complaints were not resolved by the authority within the grace period'
              stats={[{ label: 'Pending Resolution', value: complaints.length }]} />
            {error && <div style={s.alertErr}>{error}</div>}
            <SectionCard title='Complaints Requiring Action' delay='0.1s'>
              {!complaints.length ? (
                <EmptyState text='No escalated complaints' sub='All complaints are being handled' />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {complaints.map((c, i) => (
                    <div key={c.id} style={{ padding: '16px 18px', borderRadius: 14,
                      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
                      borderLeft: '3px solid #ef4444', animation: 'fadeUp 0.4s ease-out both',
                      animationDelay: (i * 0.05) + 's' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444',
                              boxShadow: '0 0 6px #ef4444', display: 'inline-block' }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#fca5a5' }}>ESCALATED — AUTHORITY NEGLIGENCE</span>
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{c.title}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>{c.description?.slice(0, 120)}...</div>
                        </div>
                        <StatusBadge status={c.status} />
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                        {c.department && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
                          background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}>{c.department}</span>}
                        {c.student_name && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
                          background: 'rgba(99,102,241,0.2)', color: '#a5b4fc' }}>By: {c.student_name}</span>}
                        {c.escalated_at && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5,
                          background: 'rgba(239,68,68,0.15)', color: '#fca5a5' }}>
                          Escalated: {new Date(c.escalated_at).toLocaleDateString()}</span>}
                      </div>
                      {resolveTarget === c.id ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <textarea value={note} onChange={e => setNote(e.target.value)}
                            placeholder='Resolution note (optional)' rows={2}
                            style={{ flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, resize: 'vertical',
                              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
                              color: '#fff', fontSize: 12, outline: 'none' }} />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => { setResolveTarget(null); setNote(''); }}
                              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                                background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                            <button onClick={() => handleResolve(c.id)} disabled={resolving === c.id}
                              style={{ padding: '8px 16px', borderRadius: 8, border: 'none',
                                background: 'linear-gradient(135deg,#10b981,#059669)',
                                color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                                opacity: resolving === c.id ? 0.6 : 1 }}>
                              {resolving === c.id ? 'Resolving...' : 'Confirm Resolve'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button onClick={() => navigate('/complaints/' + c.id)}
                            style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.2)',
                              background: 'rgba(255,255,255,0.06)', color: '#fff', cursor: 'pointer', fontSize: 12 }}>
                            View Details
                          </button>
                          <button onClick={() => setResolveTarget(c.id)}
                            style={{ padding: '7px 16px', borderRadius: 8, border: 'none',
                              background: 'linear-gradient(135deg,#10b981,#059669)',
                              color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                            Mark Resolved
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EscalatedComplaints() {
  return <ThemeProvider><EscalatedComplaintsInner /></ThemeProvider>;
}
